import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell
} from 'recharts';
import { TrendingUp, Award } from 'lucide-react';

export default function TopContributorsChart({ accountNoTransactionStats = [] }) {
  // Transform the data to show top 10 contributors
  const preparedData = accountNoTransactionStats
    .map((d) => ({
      accountNo: d.accountNo || 'Unknown',
      count: Number(d.count) || 0,
      value: Number(d.value) || 0,
    }))
    .sort((a, b) => b.value - a.value) // Sort by total value
    .slice(0, 10); // Take top 10

  // Color gradient for bars
  const getBarColor = (index) => {
    const colors = [
      '#10B981', // Green for #1
      '#3B82F6', // Blue for #2
      '#8B5CF6', // Purple for #3
      '#F59E0B', // Amber for #4-10
      '#F59E0B',
      '#F59E0B',
      '#6B7280',
      '#6B7280',
      '#6B7280',
      '#6B7280',
    ];
    return colors[index] || '#6B7280';
  };

  const totalContributions = preparedData.reduce((sum, item) => sum + item.value, 0);
  const topContributor = preparedData[0];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="text-green-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">Top Contributors This Month</h3>
        </div>
        {topContributor && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
            <Award className="text-green-600" size={16} />
            <span className="text-sm font-medium text-green-900">
              #{topContributor.accountNo.slice(-4)}
            </span>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3 kpi-grid">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 mb-1">Total Contributors</p>
          <p className="text-xl font-bold text-gray-900">{preparedData.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 mb-1">Total Amount</p>
          <p className="text-xl font-bold text-green-900">
            KSh {totalContributions.toLocaleString()}
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 mb-1">Avg per Member</p>
          <p className="text-xl font-bold text-blue-900">
            KSh {preparedData.length > 0 ? Math.round(totalContributions / preparedData.length).toLocaleString() : 0}
          </p>
        </div>
      </div>

      {/* Chart */}
      {preparedData.length === 0 ? (
        <div className="h-64 flex flex-col items-center justify-center text-gray-500">
          <TrendingUp size={48} className="text-gray-300 mb-3" />
          <p className="text-sm">No contribution data available</p>
          <p className="text-xs text-gray-400 mt-1">Data will appear once members start contributing</p>
        </div>
      ) : (
        <div style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={preparedData}
              margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="accountNo" 
                angle={-45} 
                textAnchor="end" 
                interval={0} 
                height={80}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `#${value.slice(-4)}`} // Show last 4 digits
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'value') return [`KSh ${Number(value).toLocaleString()}`, 'Total Contributed'];
                  return [`${value}`, name];
                }}
                labelFormatter={(label) => `Account: ${label}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px 12px'
                }}
              />
              <Bar 
                dataKey="value" 
                name="value" 
                radius={[8, 8, 0, 0]}
              >
                {preparedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span>Top Contributor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span>2nd Place</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-purple-500"></div>
            <span>3rd Place</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-500"></div>
            <span>Others</span>
          </div>
        </div>
      </div>
    </div>
  );
}
